export type ArabicFontFamily = 'kfgqpc' | 'uthmani' | 'indopak' | 'simple'

export type ReaderPreferences = {
  arabicFont: ArabicFontFamily
  fontSize: number          // 16–96
  reciter: string           // identifier from RECITERS (e.g. 'sudais')
  edition: string           // translation edition (e.g. 'en.sahih')
  showTranslation: boolean
  wbwEnabled: boolean       // word-by-word display toggle
  viewMode: 'verses' | 'book'
  // When true, games play short synthesized UI sound effects (tap / place /
  // correct / incorrect). Default true; user-controllable from Settings. Local
  // only — not synced to the profile (no UserPreferences field for it).
  soundEffectsEnabled: boolean
  // When true, playlist playback prepends Surah 1, Ayah 1 (Bismillah) before
  // each surah. Surah 1 itself is exempt (it IS the Bismillah). Default true.
  // Local only — not synced to the profile.
  bismillahBeforeSurah: boolean
  // When true the whole app switches to a child-friendly UX (curriculum home,
  // simplified reader controls, hidden advanced features, parental PIN gate).
  // Synced to the backend so the mode persists across devices.
  kidMode: boolean
  // Size multiplier for the Arabic text in the playlist's NowPlayingVerse
  // panel. 1.0 = the design default (1.85rem xs / 2.15rem sm); 0.75 = 75 %;
  // 2.0 = 200 %. Kept separate from the reader's `fontSize` so changes
  // here don't affect the reader (and vice versa). Local only.
  nowPlayingArabicScale: number
  // Size multiplier for the TRANSLATION text in the NowPlayingVerse panel,
  // adjustable independently of the Arabic (same [0.75, 2] range). Local only.
  nowPlayingTranslationScale: number
  // Show the translation under the verse in the playlist's NowPlayingVerse
  // panel. Default true. Local only.
  nowPlayingShowTranslation: boolean
  // Auto-follow recitation in the NowPlayingVerse panel: when true (default)
  // the verse auto-advances with the audio. When false the verse is "pinned"
  // so the user can read / scroll it without it shifting. Local only.
  nowPlayingFollow: boolean
  // Device-local cap for explicit audio downloads, in MB. 0 = no limit.
  // Text pack bytes are exempt. Local only: budgets are per-device.
  offlineDataBudgetMB: number
  // When true, a surah is silently saved for offline as it plays (budget
  // and connectivity permitting). Local only.
  autoDownloadOnPlay: boolean
}

export const DEFAULT_PREFERENCES: ReaderPreferences = {
  arabicFont: 'kfgqpc',
  fontSize: 70,
  reciter: 'sudais',
  edition: 'en.sahih',
  showTranslation: true,
  wbwEnabled: false,
  viewMode: 'verses',
  soundEffectsEnabled: true,
  bismillahBeforeSurah: true,
  kidMode: false,
  nowPlayingArabicScale: 1,
  nowPlayingTranslationScale: 1,
  nowPlayingShowTranslation: true,
  nowPlayingFollow: true,
  offlineDataBudgetMB: 500,
  autoDownloadOnPlay: false,
}

// Allowed values for `offlineDataBudgetMB`, reused by the Settings UI
// (segmented control options) and storage.ts's sanitizer. 0 = unlimited.
export const OFFLINE_BUDGET_OPTIONS_MB = [100, 250, 500, 1000, 0] as const
